class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Code-review fix (2026-07-31): the Razorpay preconnect hints in <head> are scoped to
    // template.name == 'cart'/'product' (ledger #9) since Razorpay is irrelevant elsewhere and
    // both slots were being spent on every page for no reason. But this drawer's own Checkout
    // button (rendered sitewide, settings.cart_type == 'drawer') lets a shopper tap Checkout
    // directly from a collection/index/page template without ever visiting /cart or a product
    // page first - on that path there's no preconnect at all, reproducing on the cart-icon tap
    // exactly the cold-DNS+TCP+TLS-on-tap problem the ledger #9 fix was written to prevent on
    // the checkout tap. Inject the same two preconnect links here, once, only when actually
    // needed (not already on a template that has them in <head>, not already injected this
    // page load) - so the common case (every other page load) still pays nothing extra, and
    // only the drawer-open interaction on a non-cart/product page gets the warm-up, timed to
    // the earliest moment we know checkout is a plausible next step.
    if (
      !document.body.className.includes('template-cart') &&
      !document.body.className.includes('template-product') &&
      !document.getElementById('cwv-razorpay-preconnect-dynamic')
    ) {
      var raz = document.createElement('div');
      raz.id = 'cwv-razorpay-preconnect-dynamic';
      raz.style.display = 'none';
      ['https://magic-plugins.razorpay.com', 'https://api.razorpay.com'].forEach(function (href) {
        var link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
      document.head.appendChild(raz);
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // Also close the variant sheet — it only hides via the visibility cascade otherwise, so its
    // is-open state would survive and re-show when the cart is reopened.
    if (window.CartVariantSheet) window.CartVariantSheet.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState,openDrawer) {
    // Code-review fix (2026-07-31): Shopify's /cart/add.js response intermittently omits the
    // requested `sections` payload entirely (confirmed live: reproduced on /products/dog-bed-cover,
    // response.sections came back null on 1 of 2 real adds, with Shopify's own server-timing
    // header showing no "render" sub-phase ran that time - a platform-side hiccup, not something
    // this theme's code causes). The item is still added server-side (quantity/id are always
    // correct), but without this guard, getSectionsToRender().forEach() below throws
    // "Cannot read properties of null" - silently swallowed by product-form.js's outer .catch -
    // so the drawer never opens and the shopper gets zero visual confirmation their add worked.
    // Fall back to the same GET-based refresh() this class already uses elsewhere (reliable in
    // every case tested), then open the drawer ourselves since refresh()'s own renderContents()
    // call doesn't know the caller wanted it open.
    if (!parsedState.sections) {
      (async () => {
        await this.refresh(false, parsedState.item_count);
        if (openDrawer) this.open();
      })();
      return;
    }

    const isCartEmpty = parsedState.item_count === 0;

  if (isCartEmpty) {
    this.classList.add('is-empty');
  } else {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.remove('is-empty');
  }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if(openDrawer){
        this.open();
      }
    });
  }

  
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  
  async refresh(dontRefreshCartItems,items_count) {
    try {
      const response = this.getSectionsToRender().map((section) => section.id);
      const cartResponse = await fetch(`?sections=${response.join(',')}`);
      const sections = await cartResponse.json();
      let ADDTOCART_OBJECT = { sections: sections }
      this.renderContents(ADDTOCART_OBJECT);
      if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
      if(items_count<= 0){
        if (!this.classList.contains('is-empty')) this.classList.add('is-empty');
      }
    } catch (error) {
      console.log(error); // eslint-disable-line
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: this.errorMsg.textContent
        }
      }));
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
